
import { useEffect } from "react";

interface PageMetaProps {
    /** Titolo della pagina SENZA il suffisso del sito: lo aggiunge il componente. */
    title: string;
    description: string;
    path: string;
    ogImage?: string;
    /** Valore di og:type. Il default del sito è "website"; i post usano "article". */
    ogType?: "website" | "article";
    /** Pagine che non devono finire nell'indice (404). Non tocca canonical e og:url. */
    noindex?: boolean;
}

/**
 * Updates document head meta tags for SPA sub-pages.
 * Since this is a client-side SPA, this helps with:
 * - Browser tab titles
 * - Social sharing when URL is copied
 * - Plausible analytics page tracking
 *
 * Il prerender (Puppeteer) fotografa il DOM dopo gli effect, quindi questi
 * valori finiscono anche nell'HTML statico servito ai crawler.
 */
export default function PageMeta({ title, description, path, ogImage, ogType, noindex }: PageMetaProps) {
    useEffect(() => {
        const fullTitle = `${title} | Luciano Ballerano`;
        const url = `https://www.ballerano.com${path}`;

        document.title = fullTitle;

        // Update or create meta tags
        const updates: Record<string, string> = {
            'meta[name="description"]': description,
            'meta[property="og:title"]': fullTitle,
            'meta[property="og:description"]': description,
            'meta[property="twitter:title"]': fullTitle,
            'meta[property="twitter:description"]': description,
        };

        if (!noindex) {
            updates['meta[property="og:url"]'] = url;
            updates['meta[property="twitter:url"]'] = url;
            updates['link[rel="canonical"]'] = url;
        } else {
            updates['meta[name="robots"]'] = "noindex, follow";
        }

        if (ogType) {
            updates['meta[property="og:type"]'] = ogType;
        }

        if (ogImage) {
            const absoluteImage = ogImage.startsWith("http") ? ogImage : `https://www.ballerano.com${ogImage}`;
            updates['meta[property="og:image"]'] = absoluteImage;
            updates['meta[property="twitter:image"]'] = absoluteImage;
        }

        const originals: Record<string, string> = {};

        Object.entries(updates).forEach(([selector, value]) => {
            const el = document.querySelector(selector);
            if (el) {
                if (el.tagName === 'LINK') {
                    originals[selector] = el.getAttribute('href') || '';
                    el.setAttribute('href', value);
                } else {
                    originals[selector] = el.getAttribute('content') || '';
                    el.setAttribute('content', value);
                }
            }
        });

        // Restore original values on unmount
        return () => {
            Object.entries(originals).forEach(([selector, value]) => {
                const el = document.querySelector(selector);
                if (el) {
                    if (el.tagName === 'LINK') {
                        el.setAttribute('href', value);
                    } else {
                        el.setAttribute('content', value);
                    }
                }
            });
            document.title = "Luciano Ballerano | Scrivo di AI e di persone";
        };
    }, [title, description, path, ogImage, ogType, noindex]);

    return null;
}
